import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const MEAL_ICONS = { breakfast: 'Coffee', lunch: 'UtensilsCrossed', dinner: 'Moon' };
const MEAL_COLORS = { breakfast: 'amber', lunch: 'emerald', dinner: 'indigo' };

export default function MealSettings({ isAdmin }) {
  const [mealTimes, setMealTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => { fetchMealTimes(); }, []);

  const fetchMealTimes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('restaurant_meal_times')
        ?.select('*')
        ?.order('meal_type');
      if (!error) {
        setMealTimes(data || []);
        const formInit = {};
        data?.forEach(m => {
          formInit[m?.meal_type] = { start_time: m?.start_time, end_time: m?.end_time, is_active: m?.is_active };
        });
        setEditForm(formInit);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveMealTime = async (mealType) => {
    setSaving(mealType);
    setSuccessMsg('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const existing = mealTimes?.find(m => m?.meal_type === mealType);
      const payload = {
        meal_type: mealType,
        start_time: editForm?.[mealType]?.start_time,
        end_time: editForm?.[mealType]?.end_time,
        is_active: editForm?.[mealType]?.is_active ?? true,
        updated_by: user?.id,
        updated_at: new Date()?.toISOString(),
      };
      const { error } = existing
        ? await supabase?.from('restaurant_meal_times')?.update(payload)?.eq('id', existing?.id)
        : await supabase?.from('restaurant_meal_times')?.insert(payload);
      if (!error) {
        setSuccessMsg(`${mealType?.charAt(0)?.toUpperCase() + mealType?.slice(1)} time updated.`);
        fetchMealTimes();
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const updateField = (mealType, field, value) => {
    setEditForm(prev => ({ ...prev, [mealType]: { ...prev?.[mealType], [field]: value } }));
  };

  return (
    <div className="space-y-5">
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={16} className="text-amber-500" />
          <h3 className="font-semibold text-foreground">Meal Time Windows</h3>
        </div>
        <p className="text-xs text-muted-foreground">Configure the allowed scan windows for each meal period. Tickets outside these windows will be marked as expired.</p>

        {successMsg && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-2 text-sm">
            <Icon name="CheckCircle2" size={14} />
            {successMsg}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10"><Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            {MEAL_TYPES?.map(mealType => {
              const form = editForm?.[mealType] || {};
              const existing = mealTimes?.find(m => m?.meal_type === mealType);
              return (
                <div key={mealType} className={`border rounded-xl p-4 space-y-3 ${form?.is_active !== false ? `border-${MEAL_COLORS?.[mealType]}-200 bg-${MEAL_COLORS?.[mealType]}-50/30` : 'border-border bg-muted/20'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name={MEAL_ICONS?.[mealType]} size={16} className={`text-${MEAL_COLORS?.[mealType]}-600`} />
                      <span className={`font-semibold capitalize text-${MEAL_COLORS?.[mealType]}-700`}>{mealType}</span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-muted-foreground">Active</span>
                      <div
                        onClick={() => updateField(mealType, 'is_active', !(form?.is_active ?? true))}
                        className={`w-9 h-5 rounded-full transition-colors cursor-pointer ${form?.is_active !== false ? `bg-${MEAL_COLORS?.[mealType]}-400` : 'bg-slate-300'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform mt-0.5 ${form?.is_active !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </div>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Start Time</label>
                      <input type="time" value={form?.start_time || ''} onChange={e => updateField(mealType, 'start_time', e?.target?.value)}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-300" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">End Time</label>
                      <input type="time" value={form?.end_time || ''} onChange={e => updateField(mealType, 'end_time', e?.target?.value)}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-300" />
                    </div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => saveMealTime(mealType)} disabled={saving === mealType}
                      className={`w-full py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all bg-${MEAL_COLORS?.[mealType]}-500 text-white hover:opacity-90 disabled:opacity-50`}>
                      {saving === mealType ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Save" size={14} />}
                      Save {mealType?.charAt(0)?.toUpperCase() + mealType?.slice(1)} Time
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* System Rules Info */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h4 className="font-semibold text-foreground flex items-center gap-2">
          <Icon name="Info" size={15} className="text-sky-500" />
          System Rules
        </h4>
        <div className="space-y-2">
          {[
            { icon: 'ShieldCheck', text: 'One ticket can only be used once — duplicate scans are automatically blocked.' },
            { icon: 'Clock', text: 'Tickets expire automatically after the assigned meal date.' },
            { icon: 'FileText', text: 'All scan actions are logged in real time for audit purposes.' },
            { icon: 'Users', text: 'Restaurant Moderators can validate tickets but cannot edit meal rules or cardholder data.' },
          ]?.map((rule, i) => (
            <div key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
              <Icon name={rule?.icon} size={14} className="text-sky-500 mt-0.5 flex-shrink-0" />
              <span>{rule?.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
